import React from "react";
import PropTypes from "prop-types";

import Spinner from "../../Partials/Loading/Spinner";

import { darkenColor, hexToRgb } from "../../../Utils/CommonFunctions";

import "./Button.scss";

// Helper: safely get color from theme
const getColor = (primary, secondary, fallback) => {
  if (primary) return `#${primary}`;
  if (secondary) return `#${secondary}`;
  return `#${fallback}`;
};

export default function Button({ 
  children, variant = "primary", size = "md", loading = false, icon, iconPosition = "left", className = "", theme = {},
  disabled = false, type = "button", onClick, ...props
}) {
  const isDisabled = disabled || loading;

  // Determine if we need dynamic inline styles (only for themed "primary" buttons)
  const shouldUseInlineStyles = 
    variant === "primary" && 
    (theme?.bank || theme?.brand);

  let inlineStyles = {};

  if (shouldUseInlineStyles) {
    const primaryColor = getColor(
      theme?.bank?.hex_code,
      theme?.brand?.hex_code,
      "EA2466"
    );
    const borderColor = getColor(
      theme?.bank?.hex_code,
      theme?.brand?.hex_code,
      "ED1C24"
    );
    const textColor = getColor(
      theme?.bank?.text_hex_code,
      theme?.brand?.text_hex_code,
      "ffffff"
    );

    inlineStyles = {
      "--button-bg-color": primaryColor,
      "--button-border-color": darkenColor(borderColor, 15),
      "--button-text-color": textColor,
    };

    if (isDisabled) {
      inlineStyles["--button-bg-color"] = "#D9D9D9";
      inlineStyles["--button-border-color"] = "#D9D9D9";
      inlineStyles["--button-text-color"] = "#000000";
    } else if (loading) {
      const [r, g, b] = hexToRgb(primaryColor.startsWith("#") ? primaryColor.slice(1) : primaryColor);
      const bgColor = `rgba(${r}, ${g}, ${b}, 0.5)`;
      inlineStyles["--button-bg-color"] = bgColor;
      inlineStyles["--button-border-color"] = bgColor;
    }
  }

  // Map size to your SCSS class names
  const sizeClassMap = {
    sm: "smallButton",
    md: "mediumButton",
    lg: "largeButton",
    xl: "xtraLargeButton",
    full: "fullButton",
  };

  const sizeClass = sizeClassMap[size] || "mediumButton";

  // Map variant to your SCSS class names
  const variantClassMap = {
    primary: "primaryButton",
    secondary: "secondaryButton",
    outline: "outlineButton",
    disabled: "disabledButton",
    ghost: "ghostButton",
    noButton: "noButton"
  };

  const variantClass = variantClassMap[variant] || "primaryButton";

  // Combine all classes
  const buttonClasses = [
    "button", // base class
    variantClass,
    sizeClass,
    icon ? "iconWithButton" : "",
    className.trim(),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button className={buttonClasses} style={shouldUseInlineStyles ? inlineStyles : undefined} disabled={isDisabled} type={type}
      onClick={onClick} {...props}>
      {loading ? (
        <Spinner className="spinnerButton" color={shouldUseInlineStyles ? inlineStyles["--button-text-color"] : undefined} />
      ) : (
        <>
          {icon && iconPosition === "left" && <span className="iconButton">{icon}</span>}
          <span className="buttonContent">{children}</span>
          {icon && iconPosition === "right" && <span className="iconButton">{icon}</span>}
        </>
      )}
    </button>
  );
}

Button.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(["primary", "secondary", "outline", "disabled", "ghost"]),
  size: PropTypes.oneOf(["sm", "md", "lg", "xl", "full"]),
  loading: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  className: PropTypes.string,
  theme: PropTypes.shape({
    bank: PropTypes.shape({
      hex_code: PropTypes.string,
      text_hex_code: PropTypes.string,
    }),
    brand: PropTypes.shape({
      hex_code: PropTypes.string,
      text_hex_code: PropTypes.string,
    }),
  }),
  disabled: PropTypes.bool,
  type: PropTypes.string,
  onClick: PropTypes.func,
};